import { useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Input, useToast } from '@sighthost/ui';
import {
  ticketAttachmentService,
  ATTACHMENT_ACCEPT,
  type TicketAttachment,
} from '../services/ticketAttachmentService';

/**
 * Shared attachment UI for the customer + admin ticket pages (W3, 2026-08-11).
 * Chips download via EF-signed URLs; the picker is the plain @sighthost/ui
 * Input with type="file" (the SalesImportPage precedent — no bespoke upload
 * component exists in the library, and one file per message doesn't need one).
 */

function formatSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentChips({ attachments }: { attachments: TicketAttachment[] }) {
  const { toast } = useToast();
  const [opening, setOpening] = useState<string | null>(null);
  if (attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((a) => (
        <button
          key={a.id}
          type="button"
          disabled={opening === a.id}
          onClick={async () => {
            setOpening(a.id);
            try {
              const url = await ticketAttachmentService.getDownloadUrl(a.id);
              window.open(url, '_blank', 'noopener');
            } catch (err) {
              toast({
                tone: 'danger',
                title: 'Could not open attachment',
                body: err instanceof Error ? err.message : String(err),
              });
            } finally {
              setOpening(null);
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60"
          title={`Download ${a.filename}`}
        >
          <Paperclip className="h-3 w-3 shrink-0" aria-hidden />
          <span className="max-w-[180px] truncate">{a.filename}</span>
          {a.fileSizeBytes ? (
            <span className="text-muted-foreground">{formatSize(a.fileSizeBytes)}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export function AttachmentPicker({
  file,
  onFileChange,
  disabled,
}: {
  file: File | null;
  onFileChange: (f: File | null) => void;
  disabled?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  return (
    <div>
      {file ? (
        <div className="flex items-center gap-2 text-xs text-foreground">
          <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="max-w-[240px] truncate">{file.name}</span>
          <span className="text-muted-foreground">{formatSize(file.size)}</span>
          <button
            type="button"
            onClick={() => { onFileChange(null); setError(null); }}
            disabled={disabled}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Remove attachment"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : (
        <Input
          type="file"
          accept={ATTACHMENT_ACCEPT}
          disabled={disabled}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            if (!f) { onFileChange(null); setError(null); return; }
            const invalid = ticketAttachmentService.validate(f);
            if (invalid) { setError(invalid); onFileChange(null); e.target.value = ''; return; }
            setError(null);
            onFileChange(f);
          }}
          className="max-w-xs text-xs"
          aria-label="Attach a file"
        />
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        {error ? <span className="text-danger">{error}</span> : 'Optional attachment — PNG, JPG, or PDF, up to 5 MB.'}
      </p>
    </div>
  );
}
